import { useEffect, useRef } from 'react';

/**
 * Infinite scroll: when the sentinel element enters the viewport, calls loadMore.
 * Use for list pages: as user scrolls down, automatically load the next page.
 *
 * @param {React.RefObject} sentinelRef - Ref for the element at the bottom of the list
 * @param {function} loadMore - () => void, loads the next page
 * @param {boolean} hasMore - true if more items can be loaded
 * @param {boolean} loading - true while a request is in progress (prevents double-load)
 */
export function useInfiniteScroll(sentinelRef, loadMore, hasMore, loading) {
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const sentinel = sentinelRef?.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef, hasMore, loading]);
}
